import clsx from 'clsx';
import { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import layoutStyle from '../../../../components/Layout/style';
import Footer from '../footer/Footer';
import AppHeader from '../header/AppHeader';
import RedesignThemeProvider from '../../RedesignThemeProvider';
import { useRedesignLayoutStyles } from './RedesignLayout.styles';

type RedesignLayoutProps = {
  children: ReactNode;
  footer?: boolean;
  noPadding?: boolean;
  showNav?: boolean;
  isJobLayout?: boolean;
  isUserConnected?: boolean;
  classname?: string;
  isaddBackground?: boolean;
  isClassic?: boolean;
  showOverFlow?: boolean;
  disableOverflowX?: boolean;
  /** Optional element id to scroll to instead of the top (also supports `#hash` in the URL). */
  scrollAnchor?: string;
};

const RedesignLayout = ({
  children,
  footer = false,
  noPadding = false,
  showNav = true,
  isJobLayout = false,
  isUserConnected = true,
  classname,
  isaddBackground: _isaddBackground = false,
  isClassic: _isClassic = false,
  showOverFlow = false,
  disableOverflowX = false,
  scrollAnchor,
}: RedesignLayoutProps) => {
  const cls = layoutStyle();
  const classes = useRedesignLayoutStyles();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const layoutStyleProp: CSSProperties | undefined = disableOverflowX
    ? { overflowX: 'hidden' }
    : undefined;

  useEffect(() => {
    const container = scrollContainerRef.current;
    const anchorId =
      scrollAnchor ||
      (location.hash ? decodeURIComponent(location.hash.replace(/^#/, '')) : '');

    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (container) {
        container.scrollTop = 0;
      }
    };

    if (!anchorId) {
      scrollToTop();
      return;
    }

    const scrollToAnchor = () => {
      const element = document.getElementById(anchorId);
      if (!element) return false;
      element.scrollIntoView({ behavior: 'auto', block: 'start' });
      return true;
    };

    if (scrollToAnchor()) return;

    // Content may still be mounting/loading — retry briefly, then fall back to top.
    let settled = false;
    const delays = [50, 150, 400];
    const timeouts = delays.map((delay, index) =>
      window.setTimeout(() => {
        if (settled) return;
        if (scrollToAnchor()) {
          settled = true;
          return;
        }
        if (index === delays.length - 1) {
          scrollToTop();
        }
      }, delay)
    );

    return () => timeouts.forEach(clearTimeout);
  }, [location.pathname, location.search, location.hash, scrollAnchor]);

  return (
    <RedesignThemeProvider>
      <div className={clsx(cls.layout, classname)} style={layoutStyleProp}>
        <div
          className={clsx(
            cls.content,
            classes.contentBackground,
            noPadding && cls.noPadding,
          )}>
          <AppHeader showNav={showNav} isJobLayout={isJobLayout} isUserConnected={isUserConnected} />
          <div
            ref={scrollContainerRef}
            data-redesign-scroll-container={true}
            className={cls.children}
            style={{ overflow: showOverFlow ? 'visible' : 'auto' }}>
            {children}
            {footer && <Footer />}
          </div>
        </div>
      </div>
    </RedesignThemeProvider>
  );
};

export default RedesignLayout;
